import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { 
  CalendarClock, Plus, Trash2, Edit2, Loader2, 
  Pause, Play, DollarSign
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' }
];

/**
 * Single Allowance Schedule Card
 */
const AllowanceCard = ({ schedule, onEdit, onDelete, onToggle }) => {
  const nextRun = new Date(schedule.next_run);
  const isOverdue = nextRun < new Date();

  return (
    <div 
      className={cn(
        "flex flex-col p-3 sm:p-4 rounded-xl border-2 transition-all gap-3",
        schedule.is_active 
          ? "bg-white border-emerald-200" 
          : "bg-slate-50 border-slate-200 opacity-60"
      )}
      data-testid={`allowance-${schedule.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0",
          schedule.is_active ? "bg-emerald-100" : "bg-slate-200"
        )}>
          <DollarSign className={cn(
            "w-5 h-5 sm:w-6 sm:h-6",
            schedule.is_active ? "text-emerald-600" : "text-slate-400"
          )} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 text-sm sm:text-base">
            ${schedule.amount.toFixed(2)} 
            <span className="font-normal text-slate-500 text-xs sm:text-sm ml-1 sm:ml-2">
              {FREQUENCY_OPTIONS.find(f => f.value === schedule.frequency)?.label}
            </span>
          </p>
          <p className="text-xs sm:text-sm text-slate-500 truncate">
            For: <span className="font-medium">{schedule.customer_name}</span>
          </p>
          <p className={cn(
            "text-xs",
            isOverdue && schedule.is_active ? "text-amber-600" : "text-slate-400"
          )}>
            {isOverdue && schedule.is_active 
              ? "⏰ Ready to process" 
              : `Next: ${nextRun.toLocaleDateString()}`}
          </p>
        </div>
      </div>
      
      <div className="flex items-center justify-end gap-2">
        <Switch 
          checked={schedule.is_active}
          onCheckedChange={() => onToggle(schedule)}
          data-testid={`toggle-${schedule.id}`}
        />
        <Button
          size="sm"
          variant="ghost"
          className="px-2"
          onClick={() => onEdit(schedule)}
          data-testid={`edit-allowance-${schedule.id}`}
        >
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-red-500 hover:text-red-600 px-2"
          onClick={() => onDelete(schedule.id)}
          data-testid={`delete-allowance-${schedule.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Allowance Scheduler Component for Banker Portal
 */
export const AllowanceScheduler = ({ customers = [], className }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    amount: '',
    frequency: 'weekly',
    description: 'Allowance'
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/allowances`);
      setSchedules(response.data);
    } catch (error) {
      toast.error('Failed to fetch allowances');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingSchedule(null);
    setFormData({
      customer_id: customers[0]?.id || '',
      amount: '',
      frequency: 'weekly',
      description: 'Allowance'
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      customer_id: schedule.customer_id,
      amount: schedule.amount.toString(),
      frequency: schedule.frequency,
      description: schedule.description
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (editingSchedule) {
        await axios.patch(`${API}/allowances/${editingSchedule.id}`, {
          amount: parseFloat(formData.amount),
          frequency: formData.frequency,
          description: formData.description
        });
        toast.success('Allowance updated!');
      } else {
        await axios.post(`${API}/allowances`, {
          customer_id: formData.customer_id,
          amount: parseFloat(formData.amount),
          frequency: formData.frequency,
          description: formData.description
        });
        toast.success('Allowance scheduled!');
      }
      
      setDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save allowance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Delete this allowance schedule?')) return;
    
    try {
      await axios.delete(`${API}/allowances/${scheduleId}`);
      toast.success('Allowance deleted');
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to delete allowance');
    }
  };

  const handleToggle = async (schedule) => {
    try {
      await axios.patch(`${API}/allowances/${schedule.id}`, {
        is_active: !schedule.is_active
      });
      toast.success(schedule.is_active ? 'Allowance paused' : 'Allowance resumed');
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to update allowance');
    }
  };

  const handleProcessDue = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/allowances/process`);
      toast.success(response.data.message);
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to process allowances');
    } finally {
      setProcessing(false);
    }
  };

  const dueCount = schedules.filter(s => 
    s.is_active && new Date(s.next_run) < new Date()
  ).length;

  return (
    <Card className={cn("shadow-lg", className)} data-testid="allowance-scheduler-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-emerald-500" />
              Allowance Scheduler
            </CardTitle>
            <CardDescription>Set up recurring allowances</CardDescription>
          </div>
          <div className="flex gap-2">
            {dueCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-amber-600 border-amber-300"
                onClick={handleProcessDue}
                disabled={processing}
                data-testid="process-allowances-btn"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Play className="w-4 h-4 mr-1" />
                )}
                Process ({dueCount})
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleOpenCreate}
                  disabled={customers.length === 0}
                  data-testid="add-allowance-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="allowance-dialog">
                <DialogHeader>
                  <DialogTitle>
                    {editingSchedule ? 'Edit Allowance' : 'New Allowance Schedule'}
                  </DialogTitle>
                  <DialogDescription>
                    Set up automatic recurring allowance payments
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  {!editingSchedule && (
                    <div className="space-y-2">
                      <Label>Child</Label>
                      <Select 
                        value={formData.customer_id}
                        onValueChange={(v) => setFormData({ ...formData, customer_id: v })}
                      >
                        <SelectTrigger data-testid="select-customer">
                          <SelectValue placeholder="Select child" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.username}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="allowance-amount">Amount ($)</Label>
                    <Input
                      id="allowance-amount"
                      data-testid="allowance-amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="5.00"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select 
                      value={formData.frequency}
                      onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                    >
                      <SelectTrigger data-testid="select-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="allowance-desc">Description</Label>
                    <Input
                      id="allowance-desc"
                      data-testid="allowance-description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Weekly allowance"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={submitting}
                    data-testid="submit-allowance"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {editingSchedule ? 'Update' : 'Create'} Allowance
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
          </div>
        ) : customers.length === 0 ? (
          <p className="text-center text-slate-500 py-4">
            Link customers first to set up allowances
          </p>
        ) : schedules.length === 0 ? (
          <div className="text-center py-6">
            <CalendarClock className="w-12 h-12 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No allowances scheduled</p>
            <p className="text-xs text-slate-400">Click "Add" to create one</p>
          </div>
        ) : (
          <div className="space-y-3">
            {schedules.map((schedule) => (
              <AllowanceCard
                key={schedule.id}
                schedule={schedule}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AllowanceScheduler;
